import { useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { prepareSurgeryForSave, SurgeryInputItem } from '@/utils/surgery.utils';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

export interface UseProcedureActionsReturn {
  saveProcedure: (
    procedure: SurgeryInputItem,
    isMedicalHistory?: boolean
  ) => Promise<SurgeryInputItem>;

  deleteProcedure: (id: string) => Promise<void>;
}

export function useProcedureActions(
  patient: Patient,
  encounter: Encounter | undefined
): UseProcedureActionsReturn {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);

  const saveProcedure = useCallback(
    async (
      procedure: SurgeryInputItem,
      isMedicalHistory: boolean = false
    ): Promise<SurgeryInputItem> => {
      if (!encounter) {
        notifications.show({
          color: 'red',
          title: t('surgeries.encounterRequired', {
            defaultValue: 'Encounter is required to save a procedure',
          }),
          message: t('surgeries.encounterRequiredMessage', {
            defaultValue: 'Please create an encounter first',
          }),
        });
        return Promise.reject(
          new Error('Encounter is required to save a procedure')
        );
      }

      // Prepare the FHIR resource for saving
      const resource = prepareSurgeryForSave(
        patient,
        encounter,
        procedure,
        isMedicalHistory
      );

      // Create or update based on whether we have an ID
      const saved = procedure.id
        ? await medplum.updateResource(resource)
        : await medplum.createResource(resource);

      // Invalidate cache so subsequent reads get fresh data
      medplum.invalidateSearches('Procedure');

      // Convert back to SurgeryInputItem format
      const notes = saved.note?.[0]?.text || '';
      const savedInput: SurgeryInputItem = {
        id: saved.id,
        surgery: saved.code,
        performedDate: saved.performedDateTime
          ? new Date(saved.performedDateTime)
          : saved.performedPeriod?.start
            ? new Date(saved.performedPeriod.start)
            : null,
        notes,
      };

      return savedInput;
    },
    [medplum, patient, encounter, t]
  );

  const deleteProcedure = useCallback(
    async (id: string): Promise<void> => {
      // Hard delete - remove the procedure
      await medplum.deleteResource('Procedure', id);

      // Invalidate cache
      medplum.invalidateSearches('Procedure');
    },
    [medplum]
  );

  return { saveProcedure, deleteProcedure };
}
